/**
 * @lines98/client/screens/start-screen — СТАРТОВАЯ страница = СКРОЛЛ-ЛЕНДИНГ
 * (owner-directive 2026-06-18; тикет start-page-redo-and-help, Часть A).
 *
 * Прежний вид «логотип + 3 доски» (viewport-locked) ЗАБРАКОВАН owner'ом. Стартовая
 * теперь — ПРОКРУЧИВАЕМЫЙ контент-лендинг: пользователь скроллит сверху вниз через
 * секции и доходит до нижней кнопки «Начать игру». Логотип убран ЦЕЛИКОМ.
 *
 * «Веточка» (CLAUDE.md «ствол → ветки»): ТОНКАЯ ветка — монтирует ОБЩИЕ модули и
 * задаёт только секционную раскладку. Своего движка/доски/навигации НЕ верстает:
 *   - 3 ПРЕВЬЮ-карточки casual (Предсказание/Классика/Вслепую) из `lib/mode-registry`.
 *     Превью главной = СТАТИЧНЫЙ СКРИНШОТ режима (`<img>`, MODE_SCREEN_SRC) — это
 *     ОТДЕЛЬНЫЙ тонкий слой ЛЕНДИНГА, НЕ общий `components/mode-card` (ствол→ветки:
 *     mode-card снова ЖИВОЙ board-view в «Играть»; не мутируем его ради главной —
 *     fix-cycle 4, owner-playtest v5). Под скриншотом — имя режима + ОПИСАНИЕ режима
 *     (происхождение + что внутри, start-copy.modeDesc, чёрное тело). Клик → запуск
 *     режима ЧЕРЕЗ ТОТ ЖЕ путь, что лобби (`lib/mode-preview.openModePreview`);
 *   - КЛИКАБЕЛЬНЫЕ ЗАГОЛОВКИ → ФИЧИ через ствол игровой навигации (AC-A1/A4):
 *     «Играть»→mode-select, «Доска дня»→daily, «Лидерборд»→leaderboard
 *     (`lib/game-routing.applyGameRoute` — НЕ хардкод-строка пути); «Правила игры»→
 *     Помощь how-to-play (`navigateContent`). HERO — НЕ ссылка (fork-5);
 *   - ссылки «читать далее» → в статьи «Помощи» через единый
 *     `lib/content-routing.navigateContent` (origin-relative SPA-переход, не хардкод);
 *   - нижняя CTA «Начать игру» — вход в игру через тот же ствол (openModePreview
 *     дефолтного casual-режима), НЕ хардкод-строка пути;
 *   - имена 3 карточек — group-autofit `lib/autofit-text` (§8.10-B);
 *   - строки секций — из файла-источника `lib/start-copy` (HERO/«Доска дня» verbatim
 *     owner-lock), язык = ГЛОБАЛЬНЫЙ `locale` рейла (см. `startLang()` ниже).
 *
 * ЕДИНАЯ ИНДЕКСИРУЕМАЯ ГЛАВНАЯ (fix/i18n-unify-homepage, owner-решение 2026-06-19):
 * этот скролл-лендинг — ЕДИНСТВЕННАЯ поверхность голого `/` (en) / `/ru/` (ru). Прежнего
 * ДУБЛЯ (тонкий контент-лендинг `landing.md` через content-screen ПОВЕРХ этого) больше
 * НЕТ: `/` и `/<lang>/` (без slug) монтируют ИМЕННО эту стартовую (app.ts boot/popstate),
 * а content-screen рендерит только статьи Помощи (slug ≠ ''). SSG-зеркало `landing.md`
 * остаётся first-byte HTML для краулера (SEO: self-canonical + hreflang en↔ru, модель B).
 *
 * СЛЕДУЕТ РЕЙЛУ (ADR-0021 addendum-001 §D): язык секций = глобальный `locale` (store),
 * НЕ `?lang=`. Смена языка в рейле (nav-lang) → app.ts перемонтирует стартовую на новом
 * языке + пишет URL по модели B (en→`/`, ru→`/ru/`) единым стволом navigateContent/
 * content-routing. Бренд-лого → `/` (эта главная). ВИДИМЫЙ ПЕРЕКЛЮЧАТЕЛЬ ЯЗЫКА УБРАН
 * (T-I18N-10, ADR-0021 addendum-001 §E): единственный видимый переключатель — рейл.
 *
 * Секции сверху вниз (порядок строгий, owner-directive 2026-06-24 «игра-первой»):
 * HERO (ТОЛЬКО H1) → «Играть»+3 карточки → HERO-абзац (data-section="hero-text", ПОД
 * досками) → «Правила игры» → «Доска дня» → «Лидерборд» → нижняя «Начать игру» (в
 * потоке). Первый экран — про ИГРУ (H1+доски), большой hero-текст уехал под доски.
 * Каждая секция несёт `data-section="…"` (якорь для порядок-теста).
 *
 * Layout/visual — styles/start.css (§5.0 layout-plan там; лендинг СКРОЛЛИТСЯ
 * легитимно — НЕ viewport-locked; нижняя CTA НЕ sticky — в обычном потоке).
 *
 * Spec/skill: тикет start-page-redo-and-help AC-1..8; responsive-ui §0.A/§0.B/§1.2/
 *   §5.0/§5.1/§8.10/§9.4; code-structure §1 (ствол→ветки — reuse mode-card/mode-
 *   preview/content-routing/autofit).
 */

import '../styles/start.css';

import { DEFAULT_CONTENT_LANG, type ContentLang } from '@lines98/shared/config/seo';
import { contentPath } from '@lines98/shared/config/content-routes';

import {
  createBoardPreviewCard,
  type BoardPreviewCardHandle,
} from '../components/board-preview-card.js';
import { mountSiteFooter, type SiteFooterHandle } from '../components/site-footer.js';
import { attachAutofit, type AutofitHandle } from '../lib/autofit-text.js';
import {
  isContentLang,
  navigateContent,
} from '../lib/content-routing.js';
import { applyGameRoute, type GameRouteId } from '../lib/game-routing.js';
import { t } from '../lib/i18n/index.js';
import { openModePreview } from '../lib/mode-preview.js';
import {
  lobbyEntriesOfFamily,
  type ModeEntry,
} from '../lib/mode-registry.js';
import { START_COPY, type StartCopy } from '../lib/start-copy.js';
import { useGameStore } from '../store/game-store.js';
import type { GameMode } from '@lines98/shared/types/game';
import type { ScreenHandle } from './board-screen.js';


/** Хэндл одной превью-карточки лендинга (тонкая ветка на ОБЩЕМ примитиве board-preview-card). */
interface StartCardHandle {
  readonly element: HTMLElement;
  destroy(): void;
}

/** HTML-escape для текстовых позиций. */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Многоабзацное тело → отдельные `<p class="{cls}">` по пустой строке (`\n\n`).
 * Вертикальный ритм между абзацами даёт РОДИТЕЛЬ-секция (flex-column `gap: var(--m)`,
 * start.css §.start-section/§.start-hero) — отдельные `<p>` становятся соседями и
 * получают штатный gap, без своих margin/magic-px. Одноабзацный текст → один `<p>`
 * (обратная совместимость: где `\n\n` нет — поведение прежнее). Текст эскейпится (esc).
 */
function paragraphsHtml(text: string, cls: string): string {
  return text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p class="${cls}">${esc(p)}</p>`)
    .join('\n');
}

/**
 * Язык СТАРТОВОЙ-ЛЕНДИНГА = ГЛОБАЛЬНЫЙ `locale` рейла (ЕДИНСТВЕННЫЙ источник истины,
 * ADR-0021 D-2 + addendum-001 §D: «главная/контент следуют языку от рейла»). Лендинг —
 * ИНДЕКСИРУЕМАЯ ГЛАВНАЯ `/` (en) / `/ru/` (ru), которая ОБЯЗАНА перерисовываться при
 * смене языка в рейле — поэтому язык берётся ТОЛЬКО из store `locale`, НЕ из `?lang=`
 * (прежний баг fix/i18n-unify-homepage: стартовая читала `?lang=` и рейлу не следовала).
 *
 * Deep-link `/ru/` и baseline `?demo=start&lang=ru` оба СЕТЯТ глобальный `locale` на
 * boot (app.ts) ДО монтажа стартовой → здесь читаем уже верный язык, без своего `?lang=`-
 * разбора (единый источник, не дубль). locale ∈ UI_LANGS (12); лендинг-копия есть
 * только для CONTENT_LANGS (en/ru) — для языка-заглушки фолбэк на en (как
 * resolveContentPage), видимый URL остаётся `/<lang>/`.
 */
function startLang(): ContentLang {
  if (typeof window === 'undefined') return DEFAULT_CONTENT_LANG;
  const locale = useGameStore.getState().locale; // глобальный язык рейла (единый источник)
  return isContentLang(locale) ? locale : DEFAULT_CONTENT_LANG; // en для заглушки
}

/**
 * Дефолтный casual-режим для нижней CTA «Начать игру» — первая запись casual-семьи
 * из ЕДИНОГО источника mode-registry (не хардкод 'predict'-строкой): тот же путь,
 * что у карточки. lobbyEntriesOfFamily('casual') гарантированно непуст (Предсказание/
 * Классика/Вслепую) и каждый несёт `mode`.
 */
function defaultCasualMode(): GameMode {
  const first = lobbyEntriesOfFamily('casual').find((e) => e.mode);
  return first?.mode ?? 'predict';
}

export function mountStartScreen(host: HTMLElement): ScreenHandle {
  host.innerHTML = '';

  const lang = startLang();
  const copy: StartCopy = START_COPY[lang];

  const root = document.createElement('div');
  // .screen-root — ствол-класс edge-clearance (safe-area-aware, global.css).
  // Лендинг СКРОЛЛИТСЯ (см. start.css) — НЕ viewport-locked.
  root.className = 'start-screen screen-root';
  root.lang = lang;

  // Видимый переключатель языка на стартовой УБРАН (T-I18N-10, ADR-0021 addendum-001
  // §E): единственный видимый переключатель языка — глобальный рейл-контрол (nav-lang
  // под логотипом). Стартовая больше НЕ несёт свою плашку выбора языка. Язык секций
  // следует глобальному `locale` (демо `?lang=` остаётся для baseline-скриншотов).

  // === HERO (H1 ТОЛЬКО) ========================================================
  // ПЕРЕСТАНОВКА «игра-первой» (owner-directive 2026-06-24, тикет start-page-order-
  // game-first): первый экран лендинга — про ИГРУ (H1 → «Играть» → доски), НЕ про
  // текст. HERO-секция несёт ТОЛЬКО заголовок H1 (heroTitle); большой hero-абзац
  // (copy.hero) УЕХАЛ в отдельную секцию `hero-text` ПОД блок «Играть» (см. ниже).
  // H1 остаётся первым контент-узлом. HERO-заголовок — НЕ ссылка/кнопка (хук, не
  // nav-цель; fork-5).
  const hero = document.createElement('section');
  hero.className = 'start-section start-hero';
  hero.dataset.section = 'hero';
  hero.innerHTML = `<h1 class="start-hero-title">${esc(copy.heroTitle)}</h1>`;

  // === «Играть» + 3 карточки-режима ===========================================
  // Заголовок «Играть» — КЛИКАБЕЛЬНЫЙ (AC-A1) → mode-select через ствол игровой
  // навигации (applyGameRoute('play'), НЕ хардкод-строка пути).
  const play = document.createElement('section');
  play.className = 'start-section start-play';
  play.dataset.section = 'play';
  const playHeading = buildFeatureHeading(copy.playHeading, 'play');

  const cardsEl = document.createElement('div');
  cardsEl.className = 'start-cards';
  cardsEl.setAttribute('role', 'group');
  cardsEl.setAttribute('aria-label', copy.modesAria);

  const cards: StartCardHandle[] = [];
  for (const entry of lobbyEntriesOfFamily('casual')) {
    const card = buildCard(entry, lang, copy);
    cardsEl.appendChild(card.element);
    cards.push(card);
  }
  play.append(playHeading, cardsEl);

  // === HERO-абзац (ПОД досками, ПОСЛЕ «Играть») ================================
  // Вынесенная секция описательного hero-текста (owner-directive 2026-06-24): большой
  // hero-абзац стоит ПОСЛЕ блока «Играть» (H1 + 3 доски) и ДО «Правила игры» — первый
  // экран про игру, текст ниже. ОТДЕЛЬНАЯ `<section data-section="hero-text">`. Класс
  // секции — `.start-section` БЕЗ `.start-play` → попадает под reading-col-правило
  // (start.css `.start-section:not(.start-play)`): текст сужается до читаемой колонки.
  // Текст — ИЗ copy.hero через общий paragraphsHtml (ТОТ ЖЕ источник lib/start-copy,
  // verbatim, не хардкод; SEO-сохранность — секция видима, без display:none/aria-hidden).
  const heroText = document.createElement('section');
  heroText.className = 'start-section';
  heroText.dataset.section = 'hero-text';
  heroText.innerHTML = paragraphsHtml(copy.hero, 'start-hero-text');

  // === «Правила игры» (кликабельный заголовок → Помощь how-to-play) ============
  const rules = buildLinkSection({
    section: 'rules',
    heading: copy.rulesHeading,
    body: copy.rules,
    readMore: copy.readMore,
    lang,
    targetSlug: 'how-to-play',
    headingIsLink: true,
  });

  // === «Доска дня» — заголовок → daily-board через ствол игровой навигации =====
  const daily = buildLinkSection({
    section: 'daily',
    heading: copy.dailyHeading,
    body: copy.daily,
    readMore: copy.readMore,
    lang,
    targetSlug: 'daily-board',
    headingRoute: 'daily',
  });

  // === «Лидерборд» — заголовок → leaderboard через ствол игровой навигации =====
  const leaderboard = buildLinkSection({
    section: 'leaderboard',
    heading: copy.leaderboardHeading,
    body: copy.leaderboard,
    readMore: copy.readMore,
    lang,
    targetSlug: 'leaderboards',
    headingRoute: 'leaderboard',
  });

  // === Нижняя CTA «Начать игру» — В ОБЫЧНОМ ПОТОКЕ (НЕ sticky, AC-6) ===========
  const cta = document.createElement('section');
  cta.className = 'start-section start-cta';
  cta.dataset.section = 'cta';
  const ctaBtn = document.createElement('button');
  ctaBtn.type = 'button';
  ctaBtn.className = 'btn start-cta-bottom';
  ctaBtn.textContent = copy.startCta;
  // Вход в игру ЧЕРЕЗ ствол (mode-preview дефолтного casual) — НЕ хардкод-строка пути.
  ctaBtn.addEventListener('click', () => {
    void openModePreview(defaultCasualMode());
  });
  cta.appendChild(ctaBtn);

  // Порядок монтажа «игра-первой» (AC-1): hero(H1) → play(«Играть»+доски) → hero-text
  // (абзац ПОД досками) → rules → daily → leaderboard → cta.
  root.append(hero, play, heroText, rules, daily, leaderboard, cta);

  // === ПОДВАЛ юр/инфо-ссылок (Privacy Policy) — ПЕРЕИСПОЛЬЗУЕМЫЙ ПРИМИТИВ ========
  // components/site-footer (ствол→ветки): ярлык+путь из ЕДИНОГО источника (navLabel
  // страницы в бандле) → добавление языка футер НЕ трогает. Privacy — navHidden (вне
  // оглавления «Помощи»), здесь его ЕДИНСТВЕННАЯ кликабельная точка входа из игры.
  // Монтируем в root ДО reveal-gate (ниже) — попадает в общий layout-settle.
  // Подвал: Privacy (контент-ссылка) + «♥ Поддержать» (action → игровой маршрут support,
  // owner-directive 2026-06-26: вход в донат и из подвала). Подпись — каталог t() (реактивна).
  const footer: SiteFooterHandle = mountSiteFooter(root, {
    lang,
    actions: [{ label: t('game.rail.support'), onClick: () => applyGameRoute('support') }],
  });
  // REVEAL-GATE (owner-bug 2026-06-22 «плашки досок дёргаются при заходе»): прячем экран
  // до первого кадра, пока раскладка НЕ устаканилась (cqw-доски, рейл-клиренс nav-inline,
  // появление полосы прокрутки), затем показываем УЖЕ в финальном виде → никакого видимого
  // движения, «как влитое». visibility:hidden (НЕ display:none) → layout/cqw/scrollbar
  // резолвятся ПОКА скрыто; снимаем через double-rAF (после settle), как `--mounting` у
  // гномов. Это канон-фикс FOUC «render hidden, reveal when ready».
  root.dataset.mounting = '';
  host.appendChild(root);
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      root.removeAttribute('data-mounting');
    }),
  );

  // === Делегированный перехват кликов ==========================================
  // Два класса кликабельных элементов (переключатель языка УБРАН — T-I18N-10):
  //   1) `.start-feature-link` (кликабельные заголовки→ИГРОВЫЕ фичи) — data-route:
  //      навигация через ствол game-routing (applyGameRoute). НЕ хардкод-строка пути.
  //   2) `.start-link` (заголовок «Правила игры» + «читать далее») — data-slug/
  //      data-lang → SPA-переход в статью «Помощи» через navigateContent.
  const onClick = (e: MouseEvent): void => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    const target = e.target as HTMLElement | null;

    // (1) Заголовок-фича → ИГРОВАЯ навигация через ствол game-routing.
    const feat = target?.closest<HTMLElement>('.start-feature-link');
    if (feat) {
      const route = feat.getAttribute('data-route') as GameRouteId | null;
      if (route) {
        e.preventDefault();
        // ИГРОВАЯ навигация через ствол game-routing (не хардкод-строка пути): явный
        // switch по направлению заголовка (AC-A1/A4 — «Играть»→play, «Доска дня»→daily,
        // «Лидерборд»→leaderboard). applyGameRoute меняет фазу, app.ts синхронит URL.
        switch (route) {
          case 'play':
            applyGameRoute('play');
            break;
          case 'daily':
            applyGameRoute('daily');
            break;
          case 'leaderboard':
            applyGameRoute('leaderboard');
            break;
          default:
            applyGameRoute(route);
        }
        return;
      }
      return;
    }

    // (2) Контент-ссылка (заголовок «Правила игры» / «читать далее»).
    const a = target?.closest('a.start-link');
    if (!a) return;
    e.preventDefault();
    const dl = a.getAttribute('data-lang') || lang;
    const ds = a.getAttribute('data-slug') ?? '';
    navigateContent(dl, ds);
  };
  root.addEventListener('click', onClick);

  // Имена 3 карточек — group-autofit (§8.10-B): ОДИН измеренный размер на ВСЕ 3
  // `.mode-card-name` = min, вписывающийся в длиннейшее имя («Предсказание»). На
  // узком ряду single-word имя переполнило бы карточку — разрыв по буквам ЗАПРЕЩЁН
  // (§8.10-C), поэтому имя УМЕНЬШАЕТСЯ измерением, оставаясь ОДНОЙ строкой.
  // uniform → визуальная когерентность 3 карточек (§8.8); capToDesign → не раздувать
  // сверх дизайна. Reuse общего примитива (ствол→ветки), НЕ дубль логики в ветке.
  const nameAutofit: AutofitHandle = attachAutofit(root, [
    { selector: '.mode-card-name', uniform: true, mode: 'word', capToDesign: true },
  ]);

  return {
    unmount(): void {
      nameAutofit.detach();
      footer.destroy();
      root.removeEventListener('click', onClick);
      for (const c of cards) c.destroy();
      host.innerHTML = '';
    },
  };
}

/**
 * Build a casual ПРЕВЬЮ-карточку ЛЕНДИНГА. ТОНКАЯ ВЕТКА на ОБЩЕМ примитиве
 * `components/board-preview-card` (owner-directive 2026-06-25, тикет card-preview-primitive):
 * стартовая ПЕРЕИСПОЛЬЗУЕТ рабочую оболочку лобби (хром + живая мини-доска + локализованное
 * имя + paint-изоляция + приподнятость) → три бага дубля уходят сами: (а) серый ореол вокруг
 * доски (теперь карточка приподнята, тень доски сливается в глубину), (б) лаг скролла мимо
 * живых досок (content-visibility:auto в примитиве пропускает off-screen рендер), (в) имя
 * локализуется единым источником примитива (modeLabel). Прежний дубль-билдер + стили
 * `.mode-card--landing` в start.css сняты.
 *
 * Ветка ДОБАВЛЯЕТ лендинг-специфику: ОПИСАНИЕ режима под именем (descriptive inline §0.B) +
 * клик-в-игру через ствол mode-preview (тот же путь, что лобби). casual всегда unlocked.
 * Вертикаль: [ живая мини-доска ] над [ имя ] над [ описание ]. Имя — ЛОКАЛИЗОВАННЫЙ text-узел
 * (AC-S3, локализует примитив); описание — `copy.modeDesc[hintLevel]` (чёрное тело).
 */
function buildCard(entry: ModeEntry, lang: ContentLang, copy: StartCopy): StartCardHandle {
  // Общая оболочка (ствол): хром + живая доска + локализованное имя + paint-изоляция.
  const card: BoardPreviewCardHandle = createBoardPreviewCard({
    entry,
    lang,
    variant: 'mode-card--landing',
  });
  const el = card.element;
  el.setAttribute('role', 'button');
  el.tabIndex = 0;
  el.setAttribute('aria-label', `${card.name}: ${entry.hint}`);
  // Press-feedback (owner-request 2026-06-28): мягкая просадка-depress
  // (.press-feedback → scale(0.98) для .board-preview-card, press-feedback.css) —
  // единый отклик на нажатие (owner-final 2026-06-28: ripple-волна отвергнута совсем,
  // depress даёт тактильное подтверждение без перф-стоимости). casual всегда играбелен.
  el.classList.add('press-feedback');

  // Лендинг-специфика: ОПИСАНИЕ режима под именем (descriptive inline §0.B, чёрное тело).
  // В тело примитива (под строку имени). Имя — group-autofit по `.mode-card-name` (§8.10-B).
  const desc = copy.modeDesc[entry.hintLevel];
  const descEl = document.createElement('p');
  descEl.className = 'mode-card-desc';
  descEl.textContent = desc;
  card.bodyEl.appendChild(descEl);

  // Клик/Enter/Space → запуск режима через ствол (casual всегда играбелен).
  const onClick = (): void => {
    if (entry.mode) startMode(entry.mode);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (entry.mode) startMode(entry.mode);
    }
  };
  el.addEventListener('click', onClick);
  el.addEventListener('keydown', onKey);

  return {
    element: el,
    destroy(): void {
      el.removeEventListener('click', onClick);
      el.removeEventListener('keydown', onKey);
      card.destroy();
    },
  };
}

/**
 * Кликабельный заголовок секции, ведущий в ИГРОВУЮ ФИЧУ через ствол игровой
 * навигации (AC-A1/A4). Рендерится как `<button>` (доступная кликабельная цель,
 * НЕ простой `<h2>`); делегированный клик экрана читает `data-route` → applyGameRoute
 * (НЕ хардкод-строка пути). Класс `.start-section-heading` сохраняет вид заголовка.
 */
function buildFeatureHeading(label: string, route: GameRouteId): HTMLElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'start-section-heading start-heading-link start-feature-link';
  btn.dataset.route = route;
  btn.textContent = label;
  return btn;
}

interface LinkSectionOptions {
  readonly section: string;
  readonly heading: string;
  readonly body: string;
  readonly readMore: string;
  readonly lang: ContentLang;
  readonly targetSlug: string;
  /** Заголовок секции сам кликабелен → статья «Помощи» (для «Правила игры»). */
  readonly headingIsLink?: boolean;
  /** Заголовок секции ведёт в ИГРОВУЮ фичу через ствол (daily / leaderboard). */
  readonly headingRoute?: GameRouteId;
}

/**
 * Секция-блок «заголовок + абзац + читать далее». ТРИ вида заголовка:
 *   - headingRoute → кликабельный `<button.start-feature-link>` → ИГРОВАЯ фича
 *     через ствол game-routing (AC-A4: «Доска дня»→daily, «Лидерборд»→leaderboard);
 *   - headingIsLink → кликабельная `<a.start-link>` → статья «Помощи» (navigateContent;
 *     AC-A4 «Правила игры»→how-to-play);
 *   - иначе → простой `<h2>` (не-ссылка).
 * «читать далее» — ВСЕГДА `<a.start-link>` → статья «Помощи» (явное разделение
 * заголовок=фича / читать-далее=статья). href origin-relative `/{lang}/{slug}/`.
 */
function buildLinkSection(opts: LinkSectionOptions): HTMLElement {
  const sec = document.createElement('section');
  sec.className = 'start-section start-link-section';
  sec.dataset.section = opts.section;
  // href (краулер/middle-click) — через ЕДИНЫЙ источник contentPath (модель B:
  // en без префикса `/{slug}/`, не-en с префиксом). Сам SPA-переход — navigateContent.
  const href = contentPath(opts.lang, opts.targetSlug);

  let headingHtml: string;
  if (opts.headingRoute) {
    headingHtml = `<button type="button" class="start-section-heading start-heading-link start-feature-link" data-route="${esc(
      opts.headingRoute,
    )}">${esc(opts.heading)}</button>`;
  } else if (opts.headingIsLink) {
    headingHtml = `<a class="start-link start-section-heading start-heading-link" data-lang="${esc(
      opts.lang,
    )}" data-slug="${esc(opts.targetSlug)}" href="${esc(href)}">${esc(opts.heading)}</a>`;
  } else {
    headingHtml = `<h2 class="start-section-heading">${esc(opts.heading)}</h2>`;
  }

  sec.innerHTML = `
    ${headingHtml}
    ${paragraphsHtml(opts.body, 'start-section-text')}
    <a class="start-link start-read-more" data-lang="${esc(opts.lang)}" data-slug="${esc(
      opts.targetSlug,
    )}" href="${esc(href)}">${esc(opts.readMore)} →</a>
  `;
  return sec;
}

/**
 * Запуск режима ЧЕРЕЗ ТОТ ЖЕ путь, что лобби (`lib/mode-preview`): серверная сессия
 * выдаётся на вход в превью (lazy-on-preview), доска монтируется в превью. Единый
 * модуль (не дубль) — карточка стартовой и карточка лобби ведут себя одинаково.
 */
function startMode(mode: GameMode): void {
  void openModePreview(mode);
}
